import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FieldLabel, createUsePuck } from '@puckeditor/core';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import { Button, Input } from 'reactstrap';

import { showModal, showModalTop } from '../../../../actions';
import { APIgetPages } from '../../../../api/Pages';
import { thumbnailPlaceholder } from '../../../../helpers/Collections';

import RadioField from '../Common/RadioField';

const usePuckSelector = createUsePuck();

export const collectionShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  uuid: PropTypes.string,
  name: PropTypes.string,
  featured_file: PropTypes.object,
});

export const fileShape = PropTypes.shape({
  uuid: PropTypes.string,
  name: PropTypes.string,
  path: PropTypes.string,
  previewUrl: PropTypes.string,
  thumbnailUrl: PropTypes.string,
});

export const pageShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  url: PropTypes.string,
});

export const ButtonRadioField = ({
  id,
  name,
  value,
  onChange,
  field,
  readOnly,
}) => (
  <RadioField
    id={id}
    name={name}
    label={field.label}
    value={value}
    onChange={onChange}
    options={field.options}
    readOnly={readOnly}
    groupStyle={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    }}
  />
);

ButtonRadioField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  field: PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.array,
  }).isRequired,
};

ButtonRadioField.defaultProps = {
  id: '',
  name: '',
  value: null,
  readOnly: false,
};

const TargetCollectionField = ({
  value,
  onChange,
  field,
  onShowModal,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const targetType = selectedItem?.props?.targetType;
  const intl = useIntl();

  if (targetType !== 'collection') {
    return <div className="puck-button-hidden" />;
  }

  const openModal = () => {
    onShowModal('CollectionSelectModal', {
      className: 'modal-collection-select',
      backdrop: true,
      handleCollectionSelect: (selected) => {
        onChange(selected || null);
      },
    });
  };

  return (
    <div className="puck-collection-picker-label">
      <FieldLabel label={field.label}>
        {!value && (
          <Button size="sm" color="primary" onClick={openModal} className="w-100">
            <i className="far fa-folder-open mr-1" />
            {intl.formatMessage({ defaultMessage: 'Select Collection' })}
          </Button>
        )}
        {value && (
          <div className="puck-feed-collection-item">
            <img
              src={value.featured_file ? value.featured_file.urls.thumbnail : thumbnailPlaceholder}
              alt={value.name}
              className="puck-feed-collection-thumb"
            />
            <span className="puck-feed-collection-name">{value.name}</span>
            <Button
              size="sm"
              color="link"
              className="puck-feed-collection-remove"
              onClick={() => onChange(null)}
              title={intl.formatMessage({ defaultMessage: 'Remove' })}
            >
              <i className="far fa-trash mr-0" />
            </Button>
          </div>
        )}
      </FieldLabel>
    </div>
  );
};

TargetCollectionField.propTypes = {
  value: collectionShape,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
  onShowModal: PropTypes.func.isRequired,
};

TargetCollectionField.defaultProps = {
  value: null,
};

const TargetFileField = ({
  value,
  onChange,
  field,
  onShowModalTop,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const targetType = selectedItem?.props?.targetType;
  const intl = useIntl();

  if (targetType !== 'file') {
    return <div className="puck-button-hidden" />;
  }

  const openModal = () => {
    onShowModalTop('FileSelectModalEnhanced', {
      className: 'modal-select-file modal-select-file-enhanced',
      handleSelectFile: () => {},
      handleFileData: (fileData) => {
        if (!fileData) return;
        onChange({
          uuid: fileData.uuid,
          name: fileData.original_name || fileData.name || '',
          path: fileData.path,
          previewUrl: fileData.urls?.preview,
          thumbnailUrl: fileData.urls?.thumbnail,
        });
      },
      allowUpload: false,
    });
  };

  return (
    <div className="puck-file-field-label">
      <FieldLabel label={field.label}>
        {value?.thumbnailUrl && (
          <div className="puck-file-preview">
            <img
              src={value.thumbnailUrl}
              alt={intl.formatMessage({ defaultMessage: 'Selected file' })}
              className="puck-file-preview-img"
            />
          </div>
        )}
        <div className="puck-file-picker-buttons">
          <Button size="sm" color="primary" onClick={openModal} className="w-100 mb-1">
            {value
              ? intl.formatMessage({ defaultMessage: 'Change file' })
              : intl.formatMessage({ defaultMessage: 'Select file' })}
          </Button>
          {value && (
            <Button size="sm" color="secondary" onClick={() => onChange(null)} className="w-100">
              {intl.formatMessage({ defaultMessage: 'Remove' })}
            </Button>
          )}
        </div>
      </FieldLabel>
    </div>
  );
};

TargetFileField.propTypes = {
  value: fileShape,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
  onShowModalTop: PropTypes.func.isRequired,
};

TargetFileField.defaultProps = {
  value: null,
};

export const TargetUrlField = ({
  value,
  onChange,
  field,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const targetType = selectedItem?.props?.targetType;

  if (targetType !== 'url') {
    return <div className="puck-button-hidden" />;
  }

  return (
    <div className="puck-file-field-label">
      <FieldLabel label={field.label}>
        <Input
          type="text"
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder="https://example.com"
        />
      </FieldLabel>
    </div>
  );
};

TargetUrlField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

TargetUrlField.defaultProps = {
  value: '',
};

export const TargetPageField = ({
  value,
  onChange,
  field,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const targetType = selectedItem?.props?.targetType;
  const intl = useIntl();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (targetType !== 'page') {
      return;
    }

    setLoading(true);
    APIgetPages({
      page: 1,
      per_page: 200,
      order_by: 'name',
      order: 'asc',
    })
      .then((response) => {
        const pageList = response?.data?.data || [];
        setOptions(pageList.map((page) => ({
          value: page.id,
          label: page.name || page.url || `#${page.id}`,
          page,
        })));
      })
      .catch(() => {
        setOptions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [targetType]);

  if (targetType !== 'page') {
    return <div className="puck-button-hidden" />;
  }

  const selectedOption = options.find((option) => option.value === value?.id) || null;

  return (
    <div className="puck-file-field-label">
      <FieldLabel label={field.label}>
        <Select
          isClearable
          isLoading={loading}
          options={options}
          value={selectedOption}
          onChange={(selected) => {
            onChange(selected ? {
              id: selected.page.id,
              name: selected.page.name,
              url: selected.page.url,
            } : null);
          }}
          placeholder={intl.formatMessage({ defaultMessage: 'Select page...' })}
          className="page-select"
          classNamePrefix="page-select"
        />
      </FieldLabel>
    </div>
  );
};

TargetPageField.propTypes = {
  value: pageShape,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

TargetPageField.defaultProps = {
  value: null,
};

const mapDispatchToProps = (dispatch) => ({
  onShowModal: (modalName, params) => dispatch(showModal(modalName, params)),
  onShowModalTop: (modalName, params) => dispatch(showModalTop(modalName, params)),
});

export const ConnectedTargetCollectionField =
  connect(null, mapDispatchToProps)(TargetCollectionField);
export const ConnectedTargetFileField = connect(null, mapDispatchToProps)(TargetFileField);
